import { useCallback, useEffect, useMemo } from 'react';
import { comparePlans } from '@repo/front-end-utils';
import type { TwinLaunchSummary } from '@repo/frontend-types';

import type { LaunchPlans } from './useLaunchPlans';
import { launchDifference, type DifferenceRow } from './versionsText';

/** What the Load confirmation's launch lacks and has against production (R2). */
export type DifferenceState =
  | { state: 'loading' }
  | { state: 'ready'; rows: DifferenceRow[] }
  | { state: 'failed' };

export interface UseLoadComparisonParams {
  /** The launch the confirmation is about; null while it is closed, and nothing is read. */
  target: TwinLaunchSummary | null;
  productionUuid: string | null;
  plans: LaunchPlans;
  twinBid: string | null;
}

/**
 * Production's and the target's plans, compared (`twin-details-page-load-launch`, R2, R5.2). Both are
 * read through the page's cache when the confirmation opens. Either read failing is the failed state
 * — half a comparison is never shown — and the result is keyed by the two uuids, so a new production
 * or another launch re-reads rather than showing the last pair's rows.
 */
export const useLoadComparison = ({
  target,
  productionUuid,
  plans,
  twinBid,
}: UseLoadComparisonParams): { difference: DifferenceState | null; retry: () => void } => {
  const targetUuid = target?.launchUuid ?? null;
  const { request, retry: retryPlan } = plans;
  useEffect(() => {
    if (!targetUuid || !productionUuid) return;
    request(productionUuid);
    request(targetUuid);
  }, [targetUuid, productionUuid, request]);

  // Re-reads only what failed: `retry` leaves a read or loading plan as it is.
  const retry = useCallback(() => {
    if (!targetUuid || !productionUuid) return;
    retryPlan(productionUuid);
    retryPlan(targetUuid);
  }, [targetUuid, productionUuid, retryPlan]);

  const difference = useMemo((): DifferenceState | null => {
    if (!targetUuid || !productionUuid) return null;
    const production = plans.read(productionUuid);
    const launch = plans.read(targetUuid);
    if (production?.state === 'failed' || launch?.state === 'failed') return { state: 'failed' };
    if (production?.state !== 'ready' || launch?.state !== 'ready') return { state: 'loading' };
    return {
      state: 'ready',
      rows: launchDifference(comparePlans(production.plan, launch.plan, { twinBid })),
    };
  }, [targetUuid, productionUuid, plans, twinBid]);

  return { difference, retry };
};
